import { ArrayUtils } from "neo-common";
import { TweenLite, Ease } from "gsap";

export class CustomEase extends Ease {
    private id: string;
    private data: string;
    private lookup: Array<any>;
    private points: Array<any>;
    private rawBezier: Array<number>;

    private fast: boolean;
    private l: number;

    private _numbersExp = /(?:(-|-=|\+=)?\d*\.?\d*(?:e[\-+]?\d+)?)[0-9]/gi;
    private _svgPathExp = /[achlmqstvz]|(-?\d*\.?\d*(?:e[\-+]?\d+)?)[0-9]/gi;
    private _scientific = /[\+\-]?\d*\.?\d+e[\+\-]?\d+/gi;
    private _needsParsingExp = /[cLlsS]/g;
    private _bezierError = "CustomEase only accepts Cubic Bezier data.";

    constructor(id: string, data: string, config?: any) {
        super();

        this.id = id;
        if (id) {
            Ease.map[id] = this;
        }

        this._calcEnd = true;
        this.getRatio = this._getRatio; //speed optimization, faster lookups.

        this.setData(data, config);
    }

    private _bezierToPoints(
        x1: number,
        y1: number,
        x2: number,
        y2: number,
        x3: number,
        y3: number,
        x4: number,
        y4: number,
        threshold: number,
        points: Array<any>,
        index: number,
    ) {
        let x12 = (x1 + x2) / 2;
        let y12 = (y1 + y2) / 2;
        let x23 = (x2 + x3) / 2;
        let y23 = (y2 + y3) / 2;
        let x34 = (x3 + x4) / 2;
        let y34 = (y3 + y4) / 2;
        let x123 = (x12 + x23) / 2;
        let y123 = (y12 + y23) / 2;
        let x234 = (x23 + x34) / 2;
        let y234 = (y23 + y34) / 2;
        let x1234 = (x123 + x234) / 2;
        let y1234 = (y123 + y234) / 2;
        let dx = x4 - x1;
        let dy = y4 - y1;
        let d2 = Math.abs((x2 - x4) * dy - (y2 - y4) * dx);
        let d3 = Math.abs((x3 - x4) * dy - (y3 - y4) * dx);
        let length: number;

        if (!points) {
            points = [
                { x: x1, y: y1 },
                { x: x4, y: y4 },
            ];
            index = 1;
        }
        points.splice(index || points.length - 1, 0, { x: x1234, y: y1234 });
        if ((d2 + d3) * (d2 + d3) > threshold * (dx * dx + dy * dy)) {
            length = points.length;
            this._bezierToPoints(x1, y1, x12, y12, x123, y123, x1234, y1234, threshold, points, index);
            this._bezierToPoints(
                x1234,
                y1234,
                x234,
                y234,
                x34,
                y34,
                x4,
                y4,
                threshold,
                points,
                index + 1 + (points.length - length),
            );
        }
        return points;
    }

    private _pathDataToBezier(d: string) {
        let a: RegExpMatchArray =
            (d + "")
                .replace(this._scientific, function (m) {
                    let n = +m;
                    let result = n < 0.0001 && n > -0.0001 ? 0 : n;
                    return result.toString();
                })
                .match(this._svgPathExp) || [];

        let path: Array<any> = [];
        let relativeX = 0;
        let relativeY = 0;
        let elements = a.length;
        let l = 2;
        let i: number;
        let x: number;
        let y: number;
        let command: string;
        let isRelative: boolean;
        let segment: any;
        let startX: number;
        let startY: number;
        let prevCommand: string;
        let difX: number;
        let difY: number;
        for (i = 0; i < elements; i++) {
            prevCommand = command;
            if (isNaN(parseFloat(a[i]))) {
                command = a[i].toUpperCase();
                isRelative = command !== a[i]; //lower case means relative
            } else {
                //commands like "C" can be strung together without any new command characters between.
                i--;
            }
            x = +a[i + 1];
            y = +a[i + 2];
            if (isRelative) {
                x += relativeX;
                y += relativeY;
            }
            if (!i) {
                startX = x;
                startY = y;
            }
            if (command === "M") {
                if (segment && segment.length < 8) {
                    //if the path data was funky and just had a M with no actual drawing anywhere, skip it.
                    path.length -= 1;
                    l = 0;
                }
                relativeX = startX = x;
                relativeY = startY = y;
                segment = [x, y];
                l = 2;
                path.push(segment);
                i += 2;
                command = "L"; //an "M" with more than 2 values gets interpreted as "lineTo" commands ("L").
            } else if (command === "C") {
                if (!segment) {
                    segment = [0, 0];
                }
                segment[l++] = x;
                segment[l++] = y;
                if (!isRelative) {
                    relativeX = relativeY = 0;
                }
                segment[l++] = relativeX + parseFloat(a[i + 3]); //note: "*1" is just a fast/short way to cast the value as a Number. WAAAY faster in Chrome, slightly slower in Firefox.
                segment[l++] = relativeY + parseFloat(a[i + 4]);
                segment[l++] = relativeX = relativeX + parseFloat(a[i + 5]);
                segment[l++] = relativeY = relativeY + parseFloat(a[i + 6]);
                i += 6;
            } else if (command === "S") {
                if (prevCommand === "C" || prevCommand === "S") {
                    difX = relativeX - segment[l - 4];
                    difY = relativeY - segment[l - 3];
                    segment[l++] = relativeX + difX;
                    segment[l++] = relativeY + difY;
                } else {
                    segment[l++] = relativeX;
                    segment[l++] = relativeY;
                }
                segment[l++] = x;
                segment[l++] = y;
                if (!isRelative) {
                    relativeX = relativeY = 0;
                }
                segment[l++] = relativeX = relativeX + parseFloat(a[i + 3]);
                segment[l++] = relativeY = relativeY + parseFloat(a[i + 4]);
                i += 4;
            } else if (command === "L" || command === "Z") {
                if (command === "Z") {
                    x = startX;
                    y = startY;
                    segment.closed = true;
                }
                if (command === "L" || Math.abs(relativeX - x) > 0.5 || Math.abs(relativeY - y) > 0.5) {
                    segment[l++] = relativeX + (x - relativeX) / 3;
                    segment[l++] = relativeY + (y - relativeY) / 3;
                    segment[l++] = relativeX + ((x - relativeX) * 2) / 3;
                    segment[l++] = relativeY + ((y - relativeY) * 2) / 3;
                    segment[l++] = x;
                    segment[l++] = y;
                    if (command === "L") {
                        i += 2;
                    }
                }
                relativeX = x;
                relativeY = y;
            } else {
                throw this._bezierError;
            }
        }
        return path[0];
    }

    private _findMinimum(values: Array<number>) {
        let l = values.length,
            min = 999999999999,
            i: number;
        for (i = 1; i < l; i += 6) {
            if (+values[i] < min) {
                min = +values[i];
            }
        }
        return min;
    }

    private _normalize(values: Array<number>, height?: number, originY?: number) {
        //takes all the points and translates/scales them so that the x starts at 0 and ends at 1.
        if (!originY && originY !== 0) {
            originY = Math.max(+values[values.length - 1], +values[1]);
        }
        let tx = +values[0] * -1,
            ty = -originY,
            l = values.length,
            sx = 1 / (+values[l - 2] + tx),
            sy =
                -height ||
                (Math.abs(+values[l - 1] - +values[1]) < 0.01 * (+values[l - 2] - +values[0])
                    ? this._findMinimum(values) + ty
                    : +values[l - 1] + ty),
            i: number;
        if (sy) {
            //typically y ends at 1 (so that the end values are reached)
            sy = 1 / sy;
        } else {
            //in case the ease returns to its beginning value, scale everything proportionally
            sy = -sx;
        }
        for (i = 0; i < l; i += 2) {
            values[i] = (+values[i] + tx) * sx;
            values[i + 1] = (+values[i + 1] + ty) * sy;
        }
    }

    private _getRatio(p: number) {
        var point = this.lookup[(p * this.l) | 0] || this.lookup[this.l - 1];
        if (point.nx < p) {
            point = point.n;
        }
        let result = point.y + ((p - point.x) / point.cx) * point.cy;
        return result;
    }

    private setData(data: string, config?: any) {
        this.data = data || "0,0,1,1";
        this.points = [];
        this.lookup = [];

        let values = ArrayUtils.numberArray(this.data.match(this._numbersExp).join(";"));
        let closest = 1;

        let a1: any,
            a2: any,
            i: number,
            inc: number,
            j: number,
            point: any,
            prevPoint: any,
            p: number,
            precision: number;

        precision = (config && config.precision) || 1;
        this.fast = precision <= 1;

        if (this._needsParsingExp.test(data) || (data.indexOf("M") !== -1 && data.indexOf("C") === -1)) {
            values = this._pathDataToBezier(data);
        }

        this.l = values.length;

        if (this.l === 4) {
            values.unshift(0, 0);
            values.push(1, 1);
            this.l = 8;
        } else if ((this.l - 2) % 6) {
            throw this._bezierError;
        }
        if (+values[0] !== 0 || +values[this.l - 2] !== 1) {
            let height = config && config.height ? config.height : null;
            let originY = config && config.originY ? config.originY : null;
            this._normalize(values, height, originY);
        }

        this.rawBezier = values;

        for (i = 2; i < this.l; i += 6) {
            a1 = { x: +values[i - 2], y: +values[i - 1] };
            a2 = { x: +values[i + 4], y: +values[i + 5] };
            this.points.push(a1, a2);
            this._bezierToPoints(
                a1.x,
                a1.y,
                +values[i],
                +values[i + 1],
                +values[i + 2],
                +values[i + 3],
                a2.x,
                a2.y,
                1 / (precision * 200000),
                this.points,
                this.points.length - 1,
            );
        }

        this.l = this.points.length;
        for (i = 0; i < this.l; i++) {
            point = this.points[i];
            prevPoint = this.points[i - 1] || point;
            if (point.x > prevPoint.x || (prevPoint.y !== point.y && prevPoint.x === point.x) || point === prevPoint) {
                //if a point goes BACKWARD in time or is a duplicate, just drop it.
                prevPoint.cx = point.x - prevPoint.x; //change in x between this point and the next point (performance optimization)
                prevPoint.cy = point.y - prevPoint.y;
                prevPoint.n = point;
                prevPoint.nx = point.x; //next point's x value (performance optimization, making lookups faster in getRatio()). Remember, the lookup will always land on a spot where it's either this point or the very next one (never beyond that)
                if (
                    this.fast &&
                    i > 1 &&
                    Math.abs(prevPoint.cy / prevPoint.cx - this.points[i - 2].cy / this.points[i - 2].cx) > 2
                ) {
                    //if there's a sudden change in direction, prioritize accuracy over speed. Like a bounce ease - you don't want to risk the sampling chunks landing on each side of the bounce anchor and having it clipped off.
                    this.fast = false;
                }
                if (prevPoint.cx < closest) {
                    if (!prevPoint.cx) {
                        prevPoint.cx = 0.001; //avoids math problems in getRatio() (dividing by zero)
                        if (i === this.l - 1) {
                            //in case the final segment goes vertical RIGHT at the end, make sure we end at the end.
                            prevPoint.x -= 0.001;
                            closest = Math.min(closest, 0.001);
                            this.fast = false;
                        }
                    } else {
                        closest = prevPoint.cx;
                    }
                }
            } else {
                this.points.splice(i--, 1);
                this.l--;
            }
        }

        this.l = (1 / closest + 1) | 0;
        inc = 1 / this.l;
        j = 0;
        point = this.points[0];
        if (this.fast) {
            for (i = 0; i < this.l; i++) {
                //for fastest lookups, we just sample along the path at equal x (time) distance. Uses more memory and is slightly less accurate for anchors that don't land on the sampling points, but for the vast majority of eases it's excellent (and fast).
                p = i * inc;
                if (point.nx < p) {
                    point = this.points[++j];
                }
                a1 = point.y + ((p - point.x) / point.cx) * point.cy;
                this.lookup[i] = { x: p, cx: inc, y: a1, cy: 0, nx: 9 };
                if (i) {
                    this.lookup[i - 1].cy = a1 - this.lookup[i - 1].y;
                }
            }
            this.lookup[this.l - 1].cy = this.points[this.points.length - 1].y - a1;
        } else {
            //this option is more accurate, ensuring that EVERY anchor is hit perfectly. Clipping across a bounce, for example, would never happen.
            for (i = 0; i < this.l; i++) {
                //build a lookup table based on the smallest distance so that we can instantly find the appropriate point (well, it'll either be that point or the very next one). We'll look up based on the linear progress. So it's it's 0.5 and the lookup table has 100 elements, it'd be like lookup[Math.floor(0.5 * 100)]
                if (point.nx < i * inc) {
                    point = this.points[++j];
                }
                this.lookup[i] = point;
            }

            if (j < this.points.length - 1) {
                this.lookup[i - 1] = this.points[this.points.length - 2];
            }
        }
        this._calcEnd = this.points[this.points.length - 1].y !== 1 || this.points[0].y !== 0; //ensures that we don't run into floating point errors. As long as we're starting at 0 and ending at 1, tell GSAP to skip the final calculation and use 0/1 as the factor.
    }

    public static create(id: string, data: string, config?: any): CustomEase {
        return new CustomEase(id, data, config);
    }

    public static get(id: string): Ease {
        return Ease.map[id];
    }

    public static _createOwnEasing(): void {
        // let dataNeonOut = "M0,0 C0.18,0 0.259,1 0.259,1 0.259,1 0.297,0.51 0.41,0.51 0.5,0.51 0.566,1 0.566,1 0.566,1 0.595,0.783 0.657,0.783 0.705,0.783 0.75,1 0.75,1 0.75,1 0.768,0.913 0.804,0.913 0.832,0.913 0.859,1 0.859,1 0.859,1 0.869,0.968 0.891,0.968 0.909,0.968 0.924,1 0.924,1 0.924,1 0.929,0.989 0.943,0.989 0.953,0.989 0.963,1 0.963,1 0.963,1 0.965,0.997 0.973,0.997 0.981,0.997 0.986,1 0.986,1 0.986,1 0.987,0.999 0.993,0.999 0.997,0.999 1,1 1,1";
        let dataNeonOut =
            "M0,0,C0.18,0,0.259,1,0.259,1,0.259,1,0.297,0.422,0.41,0.422,0.5,0.422,0.566,1,0.566,1,0.566,1,0.595,0.67,0.657,0.67,0.705,0.67,0.75,1,0.75,1,0.75,1,0.768,0.81,0.804,0.81,0.832,0.81,0.859,1,0.859,1,0.859,1,0.869,0.912,0.891,0.912,0.909,0.912,0.924,1,0.924,1,0.924,1,0.929,0.958,0.943,0.958,0.953,0.958,0.963,1,0.963,1,0.963,1,0.965,0.997,0.973,0.997,0.981,0.997,0.986,1,0.986,1,0.986,1,0.987,0.999,0.993,0.999,0.997,0.999,1,1,1,1";
        CustomEase.create("neonOut", dataNeonOut);

        // let dataNeonIn = "M0,0 C0,0 0.003,0.001 0.007,0.001 0.013,0.001 0.014,0 0.014,0 0.014,0 0.019,0.003 0.027,0.003 0.035,0.003 0.037,0 0.037,0 0.037,0 0.047,0.011 0.057,0.011 0.071,0.011 0.076,0 0.076,0 0.076,0 0.097,0.032 0.109,0.032 0.12,0.032 0.141,0 0.141,0 0.141,0 0.168,0.087 0.196,0.087 0.232,0.087 0.25,0 0.25,0 0.25,0 0.295,0.217 0.343,0.217 0.405,0.217 0.434,0 0.434,0 0.434,0 0.5,0.49 0.59,0.49 0.703,0.49 0.758,0 0.758,0 0.758,0 0.87,1 1,1";
        let dataNeonIn =
            "M0,0,C0,0,0.003,0.001,0.007,0.001,0.013,0.001,0.014,0,0.014,0,0.014,0,0.019,0.003,0.027,0.003,0.035,0.003,0.037,0,0.037,0,0.037,0,0.047,0.044,0.057,0.044,0.071,0.044,0.076,0,0.076,0,0.076,0,0.097,0.094,0.109,0.094,0.12,0.094,0.141,0,0.141,0,0.141,0,0.172,0.2,0.2,0.2,0.236,0.2,0.25,0,0.25,0,0.25,0,0.295,0.332,0.343,0.332,0.405,0.332,0.434,0,0.434,0,0.434,0,0.5,0.586,0.59,0.586,0.703,0.586,0.758,0,0.758,0,0.758,0,0.87,1,1,1";
        CustomEase.create("neonIn", dataNeonIn);

        let dataNeonLost =
            "M0,0 C0,0 0.044,0.2 0.1,0.2 0.155,0.2 0.2,0 0.2,0 0.2,0 0.246,0.5 0.3,0.5 0.362,0.5 0.4,0 0.4,0 0.4,0 0.398,1 0.5,1 0.598,1 0.6,0 0.6,0 0.6,0 0.65,0.492 0.708,0.491 0.773,0.491 0.8,0 0.8,0 0.8,0 0.844,0.2 0.9,0.2 0.955,0.2 1,0 1,0";
        CustomEase.create("neonLost", dataNeonLost);
    }
}



/** WEBPACK FOOTER **
 ** ./Engine/Game/Components/Effects/CustomEase.ts
 **/